import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Typography,
  Paper,
  TextField,
  Button,
  Box,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Chip,
  FormControlLabel,
  Checkbox,
  Autocomplete,
  Card,
  CardContent,
} from '@mui/material';
import {
  Send,
  AttachFile,
  Group,
  Email as EmailIcon,
  Warning,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { Club, Card as MemberCard } from '../../types';
import RequireEmailVerification from '../../components/auth/RequireEmailVerification';

interface EmailFormData {
  clubId: string;
  recipients: string[];
  subject: string;
  body: string;
  sendToAll: boolean;
  attachments: File[];
}

const SendEmailPage: React.FC = () => {
  const navigate = useNavigate();
  const [clubs, setClubs] = useState<Club[]>([]);
  const [cards, setCards] = useState<MemberCard[]>([]);
  const [formData, setFormData] = useState<EmailFormData>({
    clubId: '',
    recipients: [],
    subject: '',
    body: '',
    sendToAll: false,
    attachments: [],
  });
  const [loading, setLoading] = useState(false);
  const [loadingCards, setLoadingCards] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [emailOptions, setEmailOptions] = useState<string[]>([]);

  useEffect(() => {
    fetchClubs();
  }, []);

  const fetchClubs = async () => {
    try {
      const response = await api.get<Club[]>('/clubs');
      setClubs(response.data);
    } catch (error) {
      console.error('Failed to fetch clubs:', error);
      setError('Failed to load clubs');
    }
  };

  const fetchCardsForClub = useCallback(async (clubId: string) => {
    try {
      setLoadingCards(true);
      const response = await api.get<MemberCard[]>(`/cards/club/${clubId}`);
      setCards(response.data);
      
      // Extract unique emails for autocomplete
      const emails = response.data
        .filter(card => card.email)
        .map(card => card.email);
      setEmailOptions(Array.from(new Set(emails)));
      
      // If send to all is checked, automatically select all emails
      if (formData.sendToAll) {
        setFormData(prev => ({ ...prev, recipients: emails }));
      }
    } catch (error) {
      console.error('Failed to fetch cards:', error);
      setCards([]);
      setEmailOptions([]);
    } finally {
      setLoadingCards(false);
    }
  }, [formData.sendToAll]);

  useEffect(() => {
    if (formData.clubId) {
      fetchCardsForClub(formData.clubId);
    }
  }, [formData.clubId, fetchCardsForClub]);

  const handleSendToAllChange = (checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      sendToAll: checked,
      recipients: checked ? emailOptions : [],
    }));
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      const newFiles = Array.from(event.target.files);
      setFormData(prev => ({
        ...prev,
        attachments: [...prev.attachments, ...newFiles],
      }));
    }
  };

  const handleRemoveAttachment = (index: number) => {
    setFormData(prev => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.clubId) {
      setError('Please select a club');
      return;
    }
    
    if (formData.recipients.length === 0) {
      setError('Please select at least one recipient');
      return;
    }
    
    if (!formData.subject.trim()) {
      setError('Please enter a subject');
      return;
    }
    
    if (!formData.body.trim()) {
      setError('Please enter a message');
      return;
    }

    setError('');
    setSuccess('');
    setLoading(true);

    try {
      // Prepare attachments by converting to base64
      const attachmentsData = await Promise.all(
        formData.attachments.map(async (file) => {
          const base64 = await fileToBase64(file);
          return {
            filename: file.name,
            content: base64,
          };
        })
      );

      const payload = {
        subject: formData.subject,
        html_content: formData.body.replace(/\n/g, '<br>'),
        to_emails: formData.sendToAll ? [] : formData.recipients,
        bcc_emails: formData.sendToAll ? formData.recipients : [],
        attachments: attachmentsData,
      };

      await api.post('/send-email', payload);
      
      setSuccess(`Email successfully sent to ${formData.recipients.length} recipient(s)`);
      
      // Clear form after success
      setFormData(prev => ({
        ...prev,
        recipients: [],
        subject: '',
        body: '',
        sendToAll: false,
        attachments: [],
      }));
      
    } catch (error: any) {
      console.error('Failed to send email:', error);
      setError(error.response?.data?.detail || 'Failed to send email');
    } finally {
      setLoading(false);
    }
  };

  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const base64 = (reader.result as string).split(',')[1];
        resolve(base64);
      };
      reader.onerror = error => reject(error);
    });
  };

  const activeCardCount = cards.filter(card => card.active).length;
  const totalCardCount = cards.length;

  return (
    <RequireEmailVerification>
      <Container maxWidth="lg">
        <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h4">Send Custom Email</Typography>
          <Chip 
            label="BETA" 
            color="warning" 
            size="small" 
            sx={{ fontWeight: 600 }}
          />
        </Box>

        <Paper sx={{ p: 4 }}>
          <Box component="form" onSubmit={handleSubmit}>
            <Typography variant="h6" sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
              <EmailIcon />
              Compose Email
            </Typography>

            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Select Club</InputLabel>
              <Select
                value={formData.clubId}
                onChange={(e) => setFormData(prev => ({ ...prev, clubId: e.target.value }))}
                label="Select Club"
                required
              >
                {clubs.map((club) => (
                  <MenuItem key={club.record_id} value={club.record_id}>
                    {club.club_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {formData.clubId && (
              <Card sx={{ mb: 3, bgcolor: 'grey.50' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Group />
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Club Members
                      </Typography>
                      <Typography variant="h6">
                        {activeCardCount} active / {totalCardCount} total
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            )}

            <Box sx={{ mb: 3 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.sendToAll}
                    onChange={(e) => handleSendToAllChange(e.target.checked)}
                    disabled={!formData.clubId || loadingCards}
                  />
                }
                label={`Send to all active members (${activeCardCount})`}
              />
            </Box>

            {!formData.sendToAll && (
              <Autocomplete
                multiple
                options={emailOptions}
                value={formData.recipients}
                onChange={(_, newValue) => setFormData(prev => ({ ...prev, recipients: newValue }))}
                loading={loadingCards}
                disabled={!formData.clubId}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip variant="outlined" label={option} {...getTagProps({ index })} />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Recipients"
                    placeholder="Select recipients"
                    helperText="Start typing to search for members"
                    sx={{ mb: 3 }}
                  />
                )}
              />
            )}

            <TextField
              fullWidth
              label="Subject"
              value={formData.subject}
              onChange={(e) => setFormData(prev => ({ ...prev, subject: e.target.value }))}
              required
              sx={{ mb: 3 }}
            />

            <TextField
              fullWidth
              label="Message"
              value={formData.body}
              onChange={(e) => setFormData(prev => ({ ...prev, body: e.target.value }))}
              multiline
              rows={8}
              required
              sx={{ mb: 3 }}
              helperText="Plain text message. Line breaks will be preserved."
            />

            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                <Typography variant="subtitle1">Attachments</Typography>
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<AttachFile />}
                  size="small"
                >
                  Add Files
                  <input
                    type="file"
                    hidden
                    multiple
                    onChange={handleFileChange}
                  />
                </Button>
              </Box>

              {formData.attachments.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  {formData.attachments.map((file, index) => (
                    <Chip
                      key={index}
                      label={`${file.name} (${(file.size / 1024).toFixed(1)} KB)`}
                      onDelete={() => handleRemoveAttachment(index)}
                      sx={{ m: 0.5 }}
                    />
                  ))}
                </Box>
              )}
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            {success && (
              <Alert severity="success" sx={{ mb: 3 }}>
                {success}
              </Alert>
            )}

            <Alert severity="info" sx={{ mb: 3 }}>
              <strong>Important:</strong> When "Send to all" is selected, emails will be sent using BCC to protect recipient privacy.
            </Alert>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                type="submit"
                variant="contained"
                startIcon={loading ? <CircularProgress size={20} /> : <Send />}
                disabled={loading}
              >
                {loading ? 'Sending...' : 'Send Email'}
              </Button>
              <Button
                variant="outlined"
                onClick={() => navigate('/app/cards')}
                disabled={loading}
              >
                Cancel
              </Button>
            </Box>
          </Box>
        </Paper>

        <Paper sx={{ p: 3, mt: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Warning color="warning" />
            <Typography variant="h6">Admin-Only Beta Feature</Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            This feature is restricted to platform administrators only. Emails will be sent using the proper template system 
            and will respect each recipient's language preference (Italian/English) set in their personal details.
            For large recipient lists, emails may take some time to process.
          </Typography>
        </Paper>
      </Container>
    </RequireEmailVerification>
  );
};

export default SendEmailPage;